import { useAtomValue } from 'jotai';
import { useTranslation } from 'next-i18next';
import { Alert } from '@restworld/ui-ds-base';
import { cn, useAmbassadorData } from '@restworld/utils-common';

import {
  JobDescriptionCard,
  CompanyProfileButton,
  PerformanceBlock,
  Skills,
  Shifts,
  JobLocationDetails,
  SimilarJobs,
  JobInfoCard,
  JobAdInformativeText,
  ContractBlockDesktop,
  ContractBlockMobile,
  JobApplicationSteps,
} from './blocks';
import {
  useSimilarJobPositions,
  useFavoriteJobStatus,
  useJobPlacesInformation,
  useCompatibilityScore,
  useFavoriteCallback,
  useJobApplyStatus,
} from '../../hooks';
import { useIntegrateLogView, useShowIntercom } from '../../hooks';
import { WhyTrustUs } from './blocks/why-trust-us';
import { jobPositionApiDataAtom } from './job-position.atoms';
import { checkIfJobPositionIsOutsideItaly } from '../../utils';
import { PageInsiderNav as JobPositionInsiderNavigation } from '../legacy/page-insider-nav';

export const JobPositionComponent = () => {
  useSimilarJobPositions();
  useJobPlacesInformation();
  useIntegrateLogView();
  useCompatibilityScore();
  useJobApplyStatus();
  useFavoriteJobStatus();
  const { favoriteClickHandler, isJobPositionFavorited } = useFavoriteCallback();
  const ambassadorData = useAmbassadorData();
  useShowIntercom();
  const isJobPositionOutsideItaly = useCheckJobPositionOutsideItaly();
  const { t } = useTranslation('common');
  return (
    <PageContentWrapper>
      <div className="lg:hidden">
        <JobPositionInsiderNavigation
          favoriteClickHandler={favoriteClickHandler}
          isJobPositionFavorited={isJobPositionFavorited}
          isJobPositionOutsideItaly={isJobPositionOutsideItaly}
        />
      </div>
      <JobInfoCard ambassadorRefCode={ambassadorData?.ambassador_code} testId="job-position-info-card" />
      <InitialContentWrapper>
        <div className="hidden lg:block">
          <JobPositionInsiderNavigation
            favoriteClickHandler={favoriteClickHandler}
            isJobPositionFavorited={isJobPositionFavorited}
            isJobPositionOutsideItaly={isJobPositionOutsideItaly}
          />
        </div>
        {isJobPositionOutsideItaly && <Alert text={t('global_opportunity_description')} variant="success" />}
        <JobDescriptionCard />
        <CompanyProfileButton />
        <ContractBlockDesktop />
        <PerformanceBlock />
        <ContractBlockMobile />
        <WhyTrustUs />
        <Skills ambassadorRefCode={ambassadorData?.ambassador_code} />
        <Shifts />
        <JobLocationDetails />
        <hr className="text-gray-300" />
        <JobApplicationSteps ambassadorRefCode={ambassadorData?.ambassador_code} />
        <JobAdInformativeText />
        <SimilarJobs />
      </InitialContentWrapper>
    </PageContentWrapper>
  );
};
const PageContentWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('w-full flex flex-col gap-8 lg:flex lg:flex-row-reverse lg:justify-center lg:gap-10', className)}
    {...props}
  />
);
const InitialContentWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'flex flex-col gap-8 2xl:max-w-[calc(100vw_-_864px)] xl:max-w-[calc(100vw_-_664px)] lg:max-w-[calc(100vw_-_520px)] max-w-full',
      className
    )}
    {...props}
  />
);
const useCheckJobPositionOutsideItaly = () => {
  const address = useAtomValue(jobPositionApiDataAtom)?.restaurant;
  return address
    ? checkIfJobPositionIsOutsideItaly({
        address: address?.address,
        country: address?.country,
      })
    : false;
};
